import { api, apiAxios } from '../utils/axios-instance';
import type {
  ScheduledTaskView,
  CreateScheduledTaskBody as SdkCreateScheduledTaskBody,
  UpdateScheduledTaskBody as SdkUpdateScheduledTaskBody,
} from '../sdk/api';

export type RecurrenceFrequency = 'once' | 'daily' | 'weekdays' | 'weekly' | 'biweekly' | 'monthly';
export type TaskExecutionScope = 'analyst' | 'ontology_network';

export type ScheduledTask = Omit<ScheduledTaskView, 'recurrence' | 'scope' | 'result' | 'timezone'> & {
  recurrence?: RecurrenceFrequency | null;
  timezone?: string | null;
  scope?: TaskExecutionScope | null;
  result?: { text?: string; error?: string } | null;
  /**
   * True when the user has paused this schedule. Paused tasks keep their
   * configuration but do not fire until resumed.
   */
  paused: boolean;
};

export type ScheduledTaskStatusType = ScheduledTaskView['status'];

export type CreateScheduledTaskBody = Omit<SdkCreateScheduledTaskBody, 'recurrence' | 'scope'> & {
  recurrence?: RecurrenceFrequency;
  timezone?: string;
  scope?: TaskExecutionScope;
};

export type UpdateScheduledTaskBody = Omit<SdkUpdateScheduledTaskBody, 'recurrence' | 'scope'> & {
  recurrence?: RecurrenceFrequency;
  timezone?: string;
  scope?: TaskExecutionScope;
};

export interface ListScheduledTasksParams {
  /** Optional ISO date — when both `from` and `to` are omitted the API
   *  returns all of the caller's scheduled-task definitions. */
  from?: string;
  to?: string;
}

function normalizeRecurrence(value: unknown): RecurrenceFrequency | null {
  if (typeof value !== 'string') return null;
  switch (value.toUpperCase()) {
    case 'DAILY':
      return 'daily';
    case 'WEEKDAYS':
      return 'weekdays';
    case 'WEEKLY':
      return 'weekly';
    case 'BIWEEKLY':
      return 'biweekly';
    case 'MONTHLY':
      return 'monthly';
    case 'ONCE':
    default:
      return 'once';
  }
}

function normalizeScope(value: unknown): TaskExecutionScope | null {
  if (typeof value !== 'string') return null;
  switch (value.toUpperCase()) {
    case 'ONTOLOGY_NETWORK':
      return 'ontology_network';
    case 'ANALYST':
    default:
      return 'analyst';
  }
}

function normalizeTask(task: ScheduledTaskView): ScheduledTask {
  const raw = task as ScheduledTaskView & {
    recurrence?: unknown;
    timezone?: unknown;
    scope?: unknown;
    result?: unknown;
    paused?: unknown;
  };
  return {
    ...task,
    recurrence: normalizeRecurrence(raw.recurrence),
    timezone: typeof raw.timezone === 'string' ? raw.timezone : null,
    scope: normalizeScope(raw.scope),
    result: (raw.result as ScheduledTask['result']) ?? null,
    // The `paused` boolean was added to the API view in the
    // `feat/schedule-foundations` PR. It's optional on the wire shape we
    // ship for backward-compat; an absent value is treated as not paused.
    paused: raw.paused === true,
  };
}

/**
 * Lists the caller's scheduled tasks. When called with no params (or empty
 * params) returns the full set of definitions (used by the redesigned list
 * view). When `from`/`to` are provided the result is windowed by
 * `scheduledAt` (legacy calendar callers).
 *
 * Calls the REST endpoint directly because the generated SDK still types
 * `from`/`to` as required strings — sending `undefined` through the SDK
 * would yield literal "undefined" query params.
 */
export const listScheduledTasks = async (params: ListScheduledTasksParams = {}): Promise<ScheduledTask[]> => {
  const queryParams: Record<string, string> = {};
  if (params.from) queryParams.from = params.from;
  if (params.to) queryParams.to = params.to;

  const response = await apiAxios.get<ScheduledTaskView[]>('/scheduled-tasks', { params: queryParams });
  return response.data.map((task) => normalizeTask(task));
};

export const getScheduledTask = async (id: string): Promise<ScheduledTask> => {
  const response = await api.scheduledTasks.get({ id });
  return normalizeTask(response.data as ScheduledTaskView);
};

export const createScheduledTask = async (body: CreateScheduledTaskBody): Promise<ScheduledTask> => {
  const response = await api.scheduledTasks.create({
    createScheduledTaskBody: body as unknown as SdkCreateScheduledTaskBody,
  });
  return normalizeTask(response.data as ScheduledTaskView);
};

export const updateScheduledTask = async (id: string, body: UpdateScheduledTaskBody): Promise<ScheduledTask> => {
  const response = await api.scheduledTasks.update({
    id,
    updateScheduledTaskBody: body as unknown as SdkUpdateScheduledTaskBody,
  });
  return normalizeTask(response.data as ScheduledTaskView);
};

export const deleteScheduledTask = async (id: string): Promise<void> => {
  await api.scheduledTasks._delete({ id });
};

export const executeScheduledTask = async (id: string): Promise<ScheduledTask> => {
  const response = await api.scheduledTasks.execute({ id });
  return normalizeTask(response.data as ScheduledTaskView);
};

/**
 * Pauses a scheduled task — the row stays intact but its Cloud Scheduler
 * job is removed so it stops firing. Resumable via {@link resumeScheduledTask}.
 *
 * Calls the REST endpoint directly because the generated SDK does not yet
 * include the `pause` action.
 */
export const pauseScheduledTask = async (id: string): Promise<ScheduledTask> => {
  const response = await apiAxios.post<ScheduledTaskView>(`/scheduled-tasks/${id}/pause`);
  return normalizeTask(response.data);
};

/**
 * Resumes a previously-paused scheduled task. The API will advance to the
 * next future occurrence for recurring tasks.
 */
export const resumeScheduledTask = async (id: string): Promise<ScheduledTask> => {
  const response = await apiAxios.post<ScheduledTaskView>(`/scheduled-tasks/${id}/resume`);
  return normalizeTask(response.data);
};
